'use client';

import { useTranslation } from '@/features/i18n';
import { FadeOutOverlay } from '@/ui/FadeOutOverlay';
import { Artwork } from '@/ui/images/Artwork';
import { ArtworkLogo } from '@/ui/images/ArtworkLogo';
import { QueueListIcon } from '@heroicons/react/24/outline';
import { ListType, SmartListType } from '@vimond/services/content-discovery';
import { useRouter } from 'next/navigation';
import { PlaylistButton } from '../ui/PlaylistButton';

type Props = {
  playlist: ListType | SmartListType;
};

export const PlaylistCard = ({
  playlist,
  ...props
}: Props & React.ComponentPropsWithoutRef<'article'>) => {
  const { t } = useTranslation();
  const router = useRouter();
  if (!playlist) return <></>;

  return (
    <div
      onClick={() => router.push(`/playlist/${playlist.id}`)}
      className="cursor-pointer"
    >
      <article
        {...props}
        className={`group relative mx-1 h-full rounded-md transition-all ease-in ${
          props.className && props.className
        }`}
      >
        <div className="relative flex h-[230px] cursor-pointer flex-col">
          {playlist.images?.defaultUrl && (
            <Artwork
              alt=""
              src={playlist.images.defaultUrl}
              location="portal-carousel"
              fallbackToDefault
              fill
              sizes="25vw"
              className="object-cover"
            />
          )}
          <FadeOutOverlay />
          <QueueListIcon
            className="absolute left-2 top-2 h-6 w-6 xl:left-4 xl:top-4"
            color="white"
          />
          <div className="absolute bottom-0 left-2 text-left text-white lg:bottom-4 lg:left-4">
            <h1 className="lg:text-md text-shadow text-sm font-bold xl:text-lg">
              {playlist.title}
            </h1>
          </div>
        </div>

        <div className="absolute top-0 z-[100] hidden aspect-video h-[230px] text-white lg:block">
          <div className="border-background-secondary bg-background-secondary absolute bottom-0 left-0 right-0 top-0 rounded-md border opacity-0 transition-all duration-300 ease-out group-hover:scale-110 group-hover:opacity-100 group-hover:delay-700">
            <div className="absolute hidden h-full w-full flex-col group-hover:flex">
              {playlist?.images?.defaultUrl && (
                <Artwork
                  alt=""
                  src={playlist.images.defaultUrl}
                  location="portal-carousel"
                  fallbackToDefault
                  fill
                  sizes="25vw"
                  className="object-cover"
                />
              )}
              <FadeOutOverlay />
              <QueueListIcon
                className="absolute left-2 top-2 h-6 w-6 xl:left-4 xl:top-4"
                color="white"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-2">
              {playlist?.images?.defaultUrl && (
                <div className="max-w-[30%]">
                  <ArtworkLogo src={playlist?.images?.defaultUrl} />
                </div>
              )}
              <div className="flex items-center gap-1 ">
                <PlaylistButton playlistId={playlist.id} />
              </div>

              <div className="mx-1 mt-1 flex items-center gap-4 text-xs opacity-70">
                <span className="uppercase">{t('playlist')}</span>
              </div>

              <div className="mx-1 mt-1 w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs uppercase opacity-50">
                {playlist.labels?.split(',').map((label) => (
                  <span key={label} className="mr-1 ">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
